import { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import {
  DEFAULT_AURORA_COLOR_STOPS,
  type AuroraColorStops,
} from '../../constants/backgroundEffects';

const HEX_COLOR_RE = /^#[0-9a-f]{6}$/i;

/** Curated 3-stop palettes — one-click starts, custom picks refine them. */
const AURORA_PRESETS: { name: string; stops: AuroraColorStops }[] = [
  { name: 'Brainiacs', stops: DEFAULT_AURORA_COLOR_STOPS },
  { name: 'Northern Lights', stops: ['#00FA9A', '#008B8B', '#4B0082'] },
  { name: 'Ocean', stops: ['#38BDF8', '#2563EB', '#1E1B4B'] },
  { name: 'Sunset', stops: ['#FDE047', '#F97316', '#BE123C'] },
  { name: 'Violet Dusk', stops: ['#F0ABFC', '#A855F7', '#312E81'] },
];

interface AuroraColorPickerProps {
  colors: AuroraColorStops;
  onChange: (colors: AuroraColorStops) => void;
  disabled?: boolean;
}

const normalizeHex = (value: string): string | null => {
  const trimmed = value.trim();
  const withHash = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  return HEX_COLOR_RE.test(withHash) ? withHash.toUpperCase() : null;
};

/** One editable stop: native colour swatch + hex text field, invalid input keeps the last valid value. */
const ColorStopField = ({
  index,
  value,
  disabled,
  onChange,
}: {
  index: number;
  value: string;
  disabled?: boolean;
  onChange: (index: number, hex: string) => void;
}) => {
  const [draft, setDraft] = useState(value);
  const [invalid, setInvalid] = useState(false);

  // Re-sync the draft when the parent value changes externally (preset / reset).
  useEffect(() => {
    setDraft(value);
    setInvalid(false);
  }, [value]);

  const commit = (raw: string) => {
    const hex = normalizeHex(raw);
    if (hex) {
      setInvalid(false);
      setDraft(hex);
      onChange(index, hex);
    } else {
      // Keep the last valid colour; surface the mistake instead of silently discarding it.
      setDraft(raw);
      setInvalid(true);
    }
  };

  return (
    <div className="min-w-0">
      <span className="mb-1.5 block text-[10px] font-black uppercase tracking-[0.14em] text-white/45">
        {index === 0 ? 'Start' : index === 1 ? 'Middle' : 'End'}
      </span>
      <div
        className={`flex h-11 items-center gap-2 rounded-xl border bg-black/25 px-2.5 transition-colors focus-within:ring-2 ${
          invalid
            ? 'border-rose-400/45 focus-within:ring-rose-400/15'
            : 'border-white/12 focus-within:border-[#7d87ff]/55 focus-within:ring-[#5260FF]/15'
        }`}
      >
        <label
          className="relative h-7 w-7 shrink-0 cursor-pointer overflow-hidden rounded-lg border border-white/20 shadow-[inset_0_1px_0_rgba(255,255,255,0.25)]"
          style={{ backgroundColor: value }}
        >
          <input
            type="color"
            value={value}
            aria-label={`Aurora ${index === 0 ? 'start' : index === 1 ? 'middle' : 'end'} colour`}
            disabled={disabled}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            onChange={(event) => {
              setInvalid(false);
              setDraft(event.currentTarget.value.toUpperCase());
              onChange(index, event.currentTarget.value.toUpperCase());
            }}
          />
        </label>
        <input
          type="text"
          value={draft}
          inputMode="text"
          autoComplete="off"
          spellCheck={false}
          maxLength={7}
          disabled={disabled}
          aria-invalid={invalid}
          aria-label={`Aurora ${index === 0 ? 'start' : index === 1 ? 'middle' : 'end'} hex code`}
          placeholder="#EC4899"
          className="h-full min-w-0 flex-1 bg-transparent font-mono text-sm font-bold tracking-[0.08em] text-white outline-none placeholder:text-white/25"
          onChange={(event) => {
            setDraft(event.currentTarget.value);
            setInvalid(false);
          }}
          onBlur={(event) => commit(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.currentTarget.blur();
            }
          }}
        />
      </div>
      {invalid && (
        <p role="alert" className="mt-1 text-[11px] leading-4 text-rose-200">
          Six-digit hex, e.g. #EC4899
        </p>
      )}
    </div>
  );
};

/**
 * Aurora ramp editor — live gradient preview, curated presets, and 3 stop
 * pickers (start / middle / end of the shader's colour ramp).
 */
export default function AuroraColorPicker({ colors, onChange, disabled = false }: AuroraColorPickerProps) {
  // Per-stop edits merge into the full tuple here — stops never emit (index, hex)
  // to the parent, which only understands complete colour ramps.
  const handleStopChange = (index: number, hex: string) => {
    onChange(colors.map((color, i) => (i === index ? hex : color)) as AuroraColorStops);
  };

  const isDefault = colors.every((c, i) => c === DEFAULT_AURORA_COLOR_STOPS[i]);
  const gradient = `linear-gradient(90deg, ${colors[0]}, ${colors[1]}, ${colors[2]})`;

  return (
    <div>
      {/* Live preview — mirrors the ramp the shader interpolates across */}
      <div className="overflow-hidden rounded-2xl border border-white/10">
        <div
          aria-hidden="true"
          className="h-10 w-full transition-[background] duration-300"
          style={{ background: gradient }}
        />
        <div className="flex items-center justify-between bg-black/30 px-3 py-1.5">
          <span className="text-[10px] font-black uppercase tracking-[0.14em] text-white/45">
            Ramp preview
          </span>
          <span className="font-mono text-[10px] tracking-[0.06em] text-white/40">
            {colors.join(' · ')}
          </span>
        </div>
      </div>

      <div className="mt-4">
        <span className="mb-2 block text-[10px] font-black uppercase tracking-[0.14em] text-white/45">
          Presets
        </span>
        <div className="flex flex-wrap gap-2">
          {AURORA_PRESETS.map((preset) => {
            const active = colors.every((c, i) => c === preset.stops[i]);
            return (
              <button
                key={preset.name}
                type="button"
                onClick={() => onChange(preset.stops)}
                disabled={disabled}
                aria-pressed={active}
                className={`flex h-9 items-center gap-2 rounded-xl border px-3 text-xs font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-50 ${
                  active
                    ? 'border-[#5260FF]/45 bg-[#5260FF]/16 text-white'
                    : 'border-white/10 bg-white/[0.03] text-white/60 hover:border-white/20 hover:bg-white/[0.06] hover:text-white'
                }`}
              >
                <span
                  aria-hidden="true"
                  className="h-4 w-7 rounded-md border border-white/15"
                  style={{ background: `linear-gradient(90deg, ${preset.stops.join(', ')})` }}
                />
                {preset.name}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        {colors.map((color, index) => (
          <ColorStopField
            key={index}
            index={index}
            value={color}
            disabled={disabled}
            onChange={handleStopChange}
          />
        ))}
      </div>

      {!isDefault && (
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={() => onChange(DEFAULT_AURORA_COLOR_STOPS)}
            disabled={disabled}
            className="inline-flex items-center gap-1.5 rounded-lg border border-white/8 bg-white/[0.03] px-3 py-1.5 text-xs font-semibold text-white/50 transition-colors hover:border-white/16 hover:bg-white/[0.06] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Icon icon="bx:reset" className="h-3.5 w-3.5" aria-hidden="true" />
            Reset to default
          </button>
        </div>
      )}
    </div>
  );
}
